import { Link } from 'react-router-dom';
import { getProductImage } from '@/lib/productMedia';
import { isDigitalProduct } from '@/lib/productType';
import { useCart } from '@/hooks/useCart';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';
import { normalizeCurrency } from '@/lib/currency';
import { productsGridClass } from '@/lib/homeBlocks';

// ─────────────────────────────────────────────────────────────
// Grille de produits réutilisable pour la page d'accueil publique.
// Utilisée par la home « Mixte » (produits + services) et disponible
// pour d'autres pages d'accueil par type de compte.
// ─────────────────────────────────────────────────────────────

export interface HomeProduct {
  id: number;
  name: string;
  description: string | null;
  price: number;
  discount_enabled: boolean;
  discount_price: number | null;
  currency: string | null;
  media: unknown;
  stock: number | null;
  product_type: string | null;
  digital_file_url: string | null;
  menu_role?: string | null;
  created_at: string;
}

interface DisplayFns {
  display: (value: number, currency: string) => string;
  original: (value: number, currency: string) => string;
}

function ProductCard({
  product,
  monaie,
  badge,
  display,
  original,
  onAdd,
}: {
  product: HomeProduct;
  monaie: string;
  badge?: string;
  display: DisplayFns['display'];
  original: DisplayFns['original'];
  onAdd: (product: HomeProduct) => void;
}) {
  const imgSrc = getProductImage(product.media);
  const price = Number(product.price || 0);
  const promo = product.discount_enabled && product.discount_price != null;
  const eff = promo && product.discount_price != null ? Number(product.discount_price) : price;
  const digital = isDigitalProduct(product);
  const cur = normalizeCurrency(product.currency || monaie);
  const outOfStock = !digital && product.stock != null && product.stock <= 0;

  return (
    <div
      data-product-shop="true"
      className="group relative flex flex-col overflow-hidden rounded-xl border border-background-200/70 bg-background-50 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-300/80"
    >
      <Link to={`/product/${product.id}`} className="relative block aspect-[16/10] bg-background-100 overflow-hidden no-underline">
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={product.name}
            title={`${product.name} — produit`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.06]"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <i className="ri-shopping-bag-line text-3xl text-foreground-300"></i>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/5 to-transparent"></div>
        <div className="absolute top-2.5 left-2.5 flex flex-col items-start gap-1">
          {badge && (
            <span className="px-2 py-0.5 bg-accent-500 text-background-50 rounded-full text-[10px] font-bold flex items-center gap-1">
              <i className="ri-star-fill text-[9px]"></i>
              {badge}
            </span>
          )}
          {promo && (
            <span className="px-2 py-0.5 bg-primary-500 text-background-50 rounded-full text-[10px] font-bold">
              Promo
            </span>
          )}
        </div>
        {digital && (
          <span className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-background-50/90 text-secondary-700 flex items-center justify-center">
            <i className="ri-download-cloud-2-line text-xs"></i>
          </span>
        )}
        <span className="absolute bottom-2.5 left-2.5 flex items-baseline gap-1.5 rounded-full bg-background-50/95 px-2.5 py-1">
          {promo && product.discount_price != null ? (
            <>
              <span className="text-xs md:text-sm font-bold text-primary-700">{display(Number(product.discount_price), cur)}</span>
              <span className="text-[10px] text-foreground-400 line-through">{display(price, cur)}</span>
            </>
          ) : (
            <span className="text-xs md:text-sm font-bold text-primary-700">{price ? display(price, cur) : 'Gratuit'}</span>
          )}
        </span>
      </Link>
      <div className="flex flex-col flex-1 p-3.5 md:p-4">
        <Link to={`/product/${product.id}`} className="no-underline">
          <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-700 transition-colors">
            {product.name}
          </h3>
        </Link>
        {price > 0 && original(eff, cur) && (
          <span className="block mt-1 text-[10px] text-foreground-400">{original(eff, cur)}</span>
        )}
        <button
          type="button"
          onClick={() => onAdd(product)}
          disabled={outOfStock}
          className={`mt-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] md:text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
            outOfStock
              ? 'bg-background-100 text-foreground-400 cursor-not-allowed'
              : 'bg-primary-50 text-primary-700 hover:bg-primary-500 hover:text-background-50'
          }`}
        >
          <i className={digital ? 'ri-download-2-line' : 'ri-shopping-cart-line'}></i>
          {outOfStock ? 'Rupture de stock' : digital ? 'Acheter' : 'Ajouter au panier'}
        </button>
      </div>
    </div>
  );
}

export function ProductsGrid({
  products,
  monaie,
  columns = 3,
  badge,
}: {
  products: HomeProduct[];
  monaie: string;
  columns?: number;
  badge?: string;
}) {
  const { addToCart } = useCart();
  const { display, original } = useDisplayCurrency(monaie);

  const handleAdd = (product: HomeProduct) => {
    const price = Number(product.price || 0);
    const promo = product.discount_enabled && product.discount_price != null;
    const effective = promo ? Number(product.discount_price) : price;
    const digital = isDigitalProduct(product);
    addToCart({
      id: product.id,
      name: product.name,
      price: effective,
      original_price: promo ? price : null,
      media: getProductImage(product.media) || '',
      stock: digital ? 9999 : (product.stock ?? 9999),
      product_type: product.product_type || 'physical',
      digital_file_url: product.digital_file_url,
    });
  };

  return (
    <div className={productsGridClass(columns)}>
      {products.map((p) => (
        <ProductCard
          key={p.id}
          product={p}
          monaie={monaie}
          badge={badge}
          display={display}
          original={original}
          onAdd={handleAdd}
        />
      ))}
    </div>
  );
}